import type { VersionId, Version } from '../../common/model/index.tsx';
import type {
	ReleasableVersion,
	OnOpened,
	OnError,
	OnSuccess,
	OnCancel,
	ReleaseVersionValidationErrors,
} from '../model/index.tsx';

type ActionType<TYPE, PAYLOAD> = {
	type: TYPE;
	payload?: PAYLOAD;
};

// SET_CONTEXT ACTION

export const SET_CONTEXT = 'release-version-modal.state.SET_CONTEXT' as const;
export type SetContextPayload = {
	baseUrl: string;
	projectKey: string;
};
export type SetContextAction = ActionType<typeof SET_CONTEXT, SetContextPayload>;
export const setContext = (payload: SetContextPayload): SetContextAction => ({
	type: SET_CONTEXT,
	payload,
});

// SET_HANDLERS ACTION

export const SET_HANDLERS = 'release-version-modal.state.SET_HANDLERS' as const;
export type SetHandlersPayload = {
	onOpened: OnOpened;
	onError: OnError;
	onSuccess: OnSuccess;
	onCancel: OnCancel;
};
export type SetHandlersAction = ActionType<typeof SET_HANDLERS, SetHandlersPayload>;
export const setHandlers = (payload: SetHandlersPayload): SetHandlersAction => ({
	type: SET_HANDLERS,
	payload,
});

// INITIAL_REQUEST ACTION

export const INITIAL_REQUEST = 'release-version-modal.state.INITIAL_REQUEST' as const;
export type InitialRequestPayload = VersionId;
export type InitialRequestAction = ActionType<typeof INITIAL_REQUEST, InitialRequestPayload>;
export const initialRequest = (versionId: VersionId): InitialRequestAction => ({
	type: INITIAL_REQUEST,
	payload: versionId,
});

// INITIAL_REQUEST_SUCCESS ACTION

export const INITIAL_REQUEST_SUCCESS =
	'release-version-modal.state.INITIAL_REQUEST_SUCCESS' as const;
export type InitialRequestSuccessPayload = ReleasableVersion;
export type InitialRequestSuccessAction = ActionType<
	typeof INITIAL_REQUEST_SUCCESS,
	InitialRequestSuccessPayload
>;
export const initialRequestSuccess = (version: ReleasableVersion): InitialRequestSuccessAction => ({
	type: INITIAL_REQUEST_SUCCESS,
	payload: version,
});

// SET_VERSION_UNRESOLVED_ISSUES_COUNT ACTION

export const SET_VERSION_UNRESOLVED_ISSUES_COUNT =
	'release-version-modal.state.SET_VERSION_UNRESOLVED_ISSUES_COUNT' as const;
export type SetUnreleasedIssuesCountPayload = number;
export type SetUnreleasedIssuesCountAction = ActionType<
	typeof SET_VERSION_UNRESOLVED_ISSUES_COUNT,
	SetUnreleasedIssuesCountPayload
>;
export const setUnreleasedIssuesCount = (
	payload: SetUnreleasedIssuesCountPayload,
): SetUnreleasedIssuesCountAction => ({
	type: SET_VERSION_UNRESOLVED_ISSUES_COUNT,
	payload,
});

// SET_VERSION ACTION

export const SET_VERSION = 'release-version-modal.state.SET_VERSION' as const;
export type SetVersionPayload = ReleasableVersion;
export type SetVersionAction = ActionType<typeof SET_VERSION, SetVersionPayload>;
export const setVersion = (payload: SetVersionPayload): SetVersionAction => ({
	type: SET_VERSION,
	payload,
});

// SWAP_ISSUES ACTION

export const SWAP_ISSUES = 'release-version-modal.state.SWAP_ISSUES' as const;
export type SwapIssuesPayload = boolean;
export type SwapIssuesAction = ActionType<typeof SWAP_ISSUES, SwapIssuesPayload>;
export const swapIssues = (payload: SwapIssuesPayload): SwapIssuesAction => ({
	type: SWAP_ISSUES,
	payload,
});

// RELEASE_VERSION_UPDATE ACTION

export const RELEASE_VERSION_UPDATE = 'release-version-modal.state.RELEASE_VERSION_UPDATE' as const;
export type ReleaseVersionUpdatePayload = Partial<ReleasableVersion>;
export type ReleaseVersionUpdateAction = ActionType<
	typeof RELEASE_VERSION_UPDATE,
	ReleaseVersionUpdatePayload
>;
export const releaseVersionUpdate = (
	payload: ReleaseVersionUpdatePayload,
): ReleaseVersionUpdateAction => ({
	type: RELEASE_VERSION_UPDATE,
	payload,
});

// RELEASE_VERSION_REQUEST ACTION

export const RELEASE_VERSION_REQUEST =
	'release-version-modal.state.RELEASE_VERSION_REQUEST' as const;
export type ReleaseVersionRequestAction = ActionType<typeof RELEASE_VERSION_REQUEST, never>;
export const releaseVersionRequest = (): ReleaseVersionRequestAction => ({
	type: RELEASE_VERSION_REQUEST,
});

// RELEASE_VERSION_SUCCESS ACTION

export const RELEASE_VERSION_SUCCESS =
	'release-version-modal.state.RELEASE_VERSION_SUCCESS' as const;
export type ReleaseVersionSuccessPayload = Version;
export type ReleaseVersionSuccessAction = ActionType<
	typeof RELEASE_VERSION_SUCCESS,
	ReleaseVersionSuccessPayload
>;
export const releaseVersionSuccess = (
	payload: ReleaseVersionSuccessPayload,
): ReleaseVersionSuccessAction => ({
	type: RELEASE_VERSION_SUCCESS,
	payload,
});

// RELEASE_VERSION_FAILURE ACTION

export const RELEASE_VERSION_FAILURE =
	'release-version-modal.state.RELEASE_VERSION_FAILURE' as const;
export type ReleaseVersionFailurePayload =
	| {
			id: string;
	  }
	| undefined;
export type ReleaseVersionFailureAction = ActionType<
	typeof RELEASE_VERSION_FAILURE,
	ReleaseVersionFailurePayload
>;
export const releaseVersionFailure = (
	payload: ReleaseVersionFailurePayload,
): ReleaseVersionFailureAction => ({
	type: RELEASE_VERSION_FAILURE,
	payload,
});

// RELEASE_VERSION_VALIDATION_FAILURE ACTION

export const RELEASE_VERSION_VALIDATION_FAILURE =
	'release-version-modal.state.RELEASE_VERSION_VALIDATION_FAILURE' as const;
export type ReleaseVersionValidationFailurePayload = ReleaseVersionValidationErrors;
export type ReleaseVersionValidationFailureAction = ActionType<
	typeof RELEASE_VERSION_VALIDATION_FAILURE,
	ReleaseVersionValidationFailurePayload
>;
export const releaseVersionValidationFailure = (
	payload: ReleaseVersionValidationFailurePayload,
): ReleaseVersionValidationFailureAction => ({
	type: RELEASE_VERSION_VALIDATION_FAILURE,
	payload,
});

// CREATE_RELEASE_NOTES_OPTIONS ACTION

export const CREATE_RELEASE_NOTES_OPTIONS =
	'release-version-modal.state.CREATE_RELEASE_NOTES_OPTIONS' as const;
export type ReleaseSetShowCreateReleaseNotesOptionPayload = {
	shouldShowCreateReleaseNotesOption: boolean;
	hasReleaseNote: boolean;
};
export type ReleaseSetShowCreateReleaseNotesOptionAction = ActionType<
	typeof CREATE_RELEASE_NOTES_OPTIONS,
	ReleaseSetShowCreateReleaseNotesOptionPayload
>;
export const setCreateReleaseNotesOptionConfig = (
	payload: ReleaseSetShowCreateReleaseNotesOptionPayload,
): ReleaseSetShowCreateReleaseNotesOptionAction => ({
	type: CREATE_RELEASE_NOTES_OPTIONS,
	payload,
});

// RELEASE_SET_REDIRECT_TO_CREATE_RELEASE_NOTES_STATUS ACTION

export const RELEASE_SET_REDIRECT_TO_CREATE_RELEASE_NOTES_STATUS =
	'release-version-modal.state.RELEASE_SET_REDIRECT_TO_CREATE_RELEASE_NOTES_STATUS';
export type ReleaseSetRedirectToCreateReleaseNotesStatusPayload = boolean;
export type ReleaseSetRedirectToCreateReleaseNotesStatusAction = ActionType<
	typeof RELEASE_SET_REDIRECT_TO_CREATE_RELEASE_NOTES_STATUS,
	ReleaseSetRedirectToCreateReleaseNotesStatusPayload
>;
export const releaseRedirectToCreateReleaseNotesStatus = (
	payload: ReleaseSetRedirectToCreateReleaseNotesStatusPayload,
): ReleaseSetRedirectToCreateReleaseNotesStatusAction => ({
	type: RELEASE_SET_REDIRECT_TO_CREATE_RELEASE_NOTES_STATUS,
	payload,
});

export type Action =
	| SetContextAction
	| SetHandlersAction
	| InitialRequestAction
	| InitialRequestSuccessAction
	| SetUnreleasedIssuesCountAction
	| ReleaseVersionUpdateAction
	| ReleaseVersionRequestAction
	| ReleaseVersionSuccessAction
	| ReleaseVersionFailureAction
	| ReleaseVersionValidationFailureAction
	| SwapIssuesAction
	| ReleaseSetShowCreateReleaseNotesOptionAction
	| ReleaseSetRedirectToCreateReleaseNotesStatusAction;

export type DispatchAction =
	| ReleaseVersionValidationFailureAction
	| ReleaseVersionUpdateAction
	| ReleaseVersionRequestAction
	| SwapIssuesAction;
